import { blockPalette, blockTheme, ContentBlockStyles } from "@gaiaProfessionals/Widgets/HCP/ContentBlocks/HCPContentBlocks.styles";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import React, { FC } from "react";
import styled, { css } from "styled-components";

// <============== Fields Data ==============>
const enum fields {
    bgImage = "bgImage",
    icon = "icon",
    productImage = "productImage",
    btnLabel = "btnLabel",
    btnLink = "btnLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.bgImage]: string;
        [fields.icon]: string;
        [fields.productImage]: string;
        [fields.btnLabel]: string;
        [fields.btnLink]: LinkFieldValue;
    };
}

// <============== Categories Block Styled Component ==============>
interface CategoryProps {
    fields: {
        [fields.bgImage]: string;
    };
}

const CategoryItem = styled.div<CategoryProps>`
    overflow: hidden;
    position: relative;
    width: 100%;
    min-height: 225px;
    border-radius: 15px;
    background-image: url(${props => props.fields.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: grid;
    flex-direction: column;
    // Media Queries
    /* Large Devices */
    @media screen and (min-width: 1200px) {
        min-height: 240px;
    }
    /* Largest Devices, Wide Screens */
    @media screen and (min-width: 1200px) {
        min-height: 275px;
    }

    /* =============== Category Icon =============== */
    .categoryIcon {
        margin-top: 1.5rem;
        padding: 0 5%;
    }
    .categoryIcon__img {
        width: auto;
        min-height: 40px;
        height: 55px;
    }

    /* =============== Category Button =============== */
    .categoryBtn {
        margin-top: auto;
        padding: 0 40% 0 5%;
        width: 100%;
        min-height: 108px;
        display: flex;
        align-items: center;
        background: ${blockPalette.lightGrey};
        background: linear-gradient(180deg, ${blockPalette.white} 0%, ${blockPalette.lightGrey} 100%);
        // Media Queries
        /* Small Devices, Tablets */
        @media screen and (min-width: 768px) {
            padding: 0 5%;
        }
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            padding: 0 40% 0 5%;
        }
        // Sub Elements
        .btn {
            margin: 0;
        }
    }

    /* =============== Category Product Image =============== */
    .categoryProduct {
        max-width: 40%;
        position: absolute;
        justify-self: right;
        align-self: center;
        // Media Queries
        /* Large Devices, Wide Screens */
        @media screen and (min-width: 1200px) {
            margin-right: 2%;
        }
    }
    .categoryProduct__img {
        max-height: 175px;
        // Media Queries
        /* Small Devices, Tablets */
        @media screen and (min-width: 768px) {
            max-height: 225px;
            display: none;
        }
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            display: block;
        }
    }

`;

// <============== Component ==============>
const HCPCategoryBlockItem: FC<OwnProps> = ({ fields }) => {

    const { url } = useGetLink(fields.btnLink);

    return (
        <CategoryItem fields={fields}>
            <div className="categoryIcon">
                {fields.icon && <img className="categoryIcon__img" src={fields.icon} alt="icon" aria-hidden="true" />}
            </div>
            <div className="categoryBtn">
                {url && (
                    <Link className="btn btn--ghost-secondary" href={url}>
                        {fields.btnLabel}
                    </Link>
                )}
            </div>
            <div className="categoryProduct">
                {fields.productImage && <img className="categoryProduct__img" src={fields.productImage} alt="product" aria-hidden="true" />}
            </div>
        </CategoryItem>
    );
};

// <============== Widget Fields ==============>
const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const HCPCategoryItem: WidgetModule = {
    component: HCPCategoryBlockItem,
    definition: {
        group: "Common",
        icon: "Banner",
        displayName: "HCP Category Item",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.bgImage,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.icon,
                displayName: "Icon",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.productImage,
                displayName: "Product Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.btnLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Learn More",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.btnLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: contentTab,
            },
        ],
    },
};

export default HCPCategoryItem;